import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import { styled } from '@mui/material/styles';
import { Boxes, ArrowLeft } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';

const StyledCard = styled(Card)({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
});

export default function AssetEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id && id !== 'new';

  const [categories, setCategories] = useState([]);
  const [offices, setOffices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    asset_code: '',
    category_id: '',
    description: '',
    model: '',
    serial_number: '',
    purchase_date: '',
    purchase_value: '',
    supplier_id: '',
    ownership_type: 'PROPRIETA',
    office_id: '',
    status_note: ''
  });

  const loadReferences = async () => {
    try {
      const [catRes, officeRes, supplierRes] = await Promise.all([
        api.get('/asset-categories'),
        api.get('/office'),
        api.get('/asset-suppliers')
      ]);
      setCategories(catRes.data || []);
      setOffices(officeRes.data || []);
      setSuppliers(supplierRes.data || []);
    } catch (error) {
      console.error('Error loading asset references', error);
      toast.error('Errore nel caricamento dei dati di supporto');
    }
  };

  const loadAsset = async () => {
    if (!isEditMode) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get(`/assets/${id}`);
      const asset = res.data?.asset;
      if (!asset) {
        toast.error('Asset non trovato');
        navigate('/assets');
        return;
      }
      setForm({
        asset_code: asset.asset_code || '',
        category_id: asset.category_id || '',
        description: asset.description || '',
        model: asset.model || '',
        serial_number: asset.serial_number || '',
        purchase_date: asset.purchase_date || '',
        purchase_value: asset.purchase_value != null ? String(asset.purchase_value) : '',
        supplier_id: asset.supplier_id || '',
        ownership_type: asset.ownership_type || 'PROPRIETA',
        office_id: asset.office_id || '',
        status_note: asset.status_note || ''
      });
    } catch (error) {
      console.error('Error loading asset', error);
      toast.error('Errore nel caricamento dell\'asset');
      navigate('/assets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadReferences();
      await loadAsset();
      setLoading(false);
    };
    init();
  }, [id]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.asset_code || !form.category_id || !form.description) {
      toast.error('Codice asset, categoria e descrizione sono obbligatori');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        asset_code: form.asset_code,
        category_id: form.category_id,
        description: form.description,
        model: form.model || null,
        serial_number: form.serial_number || null,
        purchase_date: form.purchase_date || null,
        purchase_value: form.purchase_value ? Number(form.purchase_value) : null,
        supplier_id: form.supplier_id || null,
        ownership_type: form.ownership_type || 'PROPRIETA',
        office_id: form.office_id || null,
        status_note: form.status_note || null
      };
      if (isEditMode) {
        await api.put(`/assets/${id}`, payload);
        toast.success('Asset aggiornato');
      } else {
        await api.post('/assets', payload);
        toast.success('Asset creato');
      }
      navigate('/assets');
    } catch (error) {
      console.error('Error saving asset', error);
      toast.error('Errore nel salvataggio dell\'asset');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Button
          variant="text"
          startIcon={<ArrowLeft size={16} />}
          onClick={() => navigate('/assets')}
        >
          Indietro
        </Button>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: '#eef2ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Boxes size={20} color="#4f46e5" />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {isEditMode ? 'Modifica asset' : 'Nuovo asset'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Compila i dati principali dell\'asset aziendale
          </Typography>
        </Box>
      </Box>

      <StyledCard>
        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          <TextField
            label="Codice asset"
            value={form.asset_code}
            onChange={handleChange('asset_code')}
            size="small"
            fullWidth
            required
          />
          <FormControl size="small" fullWidth required>
            <InputLabel>Categoria</InputLabel>
            <Select
              label="Categoria"
              value={form.category_id}
              onChange={handleChange('category_id')}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Descrizione"
            value={form.description}
            onChange={handleChange('description')}
            size="small"
            fullWidth
            required
          />
          <TextField
            label="Modello"
            value={form.model}
            onChange={handleChange('model')}
            size="small"
            fullWidth
          />
          <TextField
            label="Serial number"
            value={form.serial_number}
            onChange={handleChange('serial_number')}
            size="small"
            fullWidth
          />
          <TextField
            label="Data acquisto"
            type="date"
            value={form.purchase_date}
            onChange={handleChange('purchase_date')}
            size="small"
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Valore acquisto"
            type="number"
            value={form.purchase_value}
            onChange={handleChange('purchase_value')}
            size="small"
            fullWidth
          />
          <Autocomplete
            size="small"
            options={suppliers}
            getOptionLabel={(s) => s.name}
            value={suppliers.find((s) => s.id === form.supplier_id) || null}
            onChange={(event, newValue) =>
              setForm((prev) => ({
                ...prev,
                supplier_id: newValue ? newValue.id : ''
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Fornitore"
                placeholder="Cerca o seleziona un fornitore"
                fullWidth
              />
            )}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Proprietà</InputLabel>
            <Select
              label="Proprietà"
              value={form.ownership_type}
              onChange={handleChange('ownership_type')}
            >
              <MenuItem value="PROPRIETA">Proprietà</MenuItem>
              <MenuItem value="LEASING">Leasing</MenuItem>
              <MenuItem value="NOLEGGIO">Noleggio</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Sede</InputLabel>
            <Select
              label="Sede"
              value={form.office_id}
              onChange={handleChange('office_id')}
            >
              <MenuItem value="">
                <em>Nessuna</em>
              </MenuItem>
              {offices.map((office) => (
                <MenuItem key={office.id} value={office.id}>
                  {office.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Note di stato"
            value={form.status_note}
            onChange={handleChange('status_note')}
            size="small"
            multiline
            minRows={2}
            fullWidth
          />
          <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 2' }, display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
            <Button onClick={() => navigate('/assets')}>
              Annulla
            </Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {isEditMode ? 'Salva modifiche' : 'Crea asset'}
            </Button>
          </Box>
        </Box>
      </StyledCard>
    </Box>
  );
}
